import { useMemo } from "react";
import { Circle } from "../AHTheme/Circle";
import { AHText, AHTextProps } from "../Text/AHText";
import { AHIcon, AHIconType } from "../AHIcon";

import { PublicOrPrivateImage } from "../Image";
import { ImagePublicOrPrivate_image$key } from "../__generated__/ImagePublicOrPrivate_image.graphql";
import { ColorType } from "../AHTheme/AHTheme";
import { graphql, useFragment } from "react-relay";
import { Avatar_employee$key } from "./__generated__/Avatar_employee.graphql";
import { CircleSize, useCircleSize } from "../LayoutProvider";
import { Avatar_user$key } from "./__generated__/Avatar_user.graphql";
import { Avatar_actor$key } from "./__generated__/Avatar_actor.graphql";
import { Avatar_groupActor$key } from "./__generated__/Avatar_groupActor.graphql";
import { emojiAwareInitials } from "../../common/emojiAwareInitials";
import { Avatar_connectActor$key } from "./__generated__/Avatar_connectActor.graphql";
import { View } from "react-native";
import { useScaleFactor } from "../AHScaleFactor";

type AvatarSize = "small" | "medium" | "large";
type AvatarStyle = "assigned-to-me" | "default";

type SharedAvatarProps = {
  size: AvatarSize;
  disableBorder?: boolean;
  style?: "assigned-to-me" | "default";
};
function getAvatarSize(size: AvatarSize): CircleSize {
  switch (size) {
    case "small":
      return "smallMedium";
    case "medium":
      return "medium";
    case "large":
      return "large";
  }
}
function AvatarCircle(props: {
  children?: React.ReactNode;
  size: AvatarSize;
  background: "image" | "solid";
  style: AvatarStyle;
  disableBorder?: boolean;
  activeBadge?: boolean;
}) {
  const size = useMemo(() => getAvatarSize(props.size), [props.size]);

  const borderColor = useMemo((): ColorType => {
    if (props.style === "assigned-to-me") {
      return "text-primary";
    }
    return "border-primary";
  }, [props.style]);

  return (
    <View style={{ position: "relative" }}>
      <Circle
        size={size}
        color={props.style === "assigned-to-me" ? "surface-dark" : "surface"}
        borderWidth={props.disableBorder ? 0 : 1}
        borderColor={borderColor}
        style={{
          overflow: "hidden",
        }}
      >
        {props.children}
      </Circle>
      {props.activeBadge ? (
        <Circle
          style={{
            position: "absolute",
            bottom: 0,
            right: 0,
          }}
          size="dot"
          color="positive"
        />
      ) : null}
    </View>
  );
}

function avatarTextColor(style: AvatarStyle): ColorType {
  if (style === "assigned-to-me") {
    return "text-inverted";
  }
  return "text-secondary";
}
function AvatarInitials(props: {
  fullName: string;
  size: AvatarSize;
  style: AvatarStyle;
}) {
  const textType = useMemo((): AHTextProps["type"] => {
    switch (props.size) {
      case "large":
        return "title-small";
      case "medium":
        return "subtitle";
      case "small":
      default:
        return "info-small";
    }
  }, [props.size]);
  return (
    <AHText type={textType} color={avatarTextColor(props.style)}>
      {emojiAwareInitials(props.fullName)}
    </AHText>
  );
}

function AvatarIcon(props: {
  icon: AHIconType;
  size: AvatarSize;
  style: AvatarStyle;
}) {
  const size = useMemo(() => getAvatarSize(props.size), [props.size]);
  const circleSize = useCircleSize(size);
  return (
    <AHIcon
      type={props.icon}
      forceSize={circleSize / 2}
      color={avatarTextColor(props.style)}
    />
  );
}

function AvatarImage(props: {
  image: ImagePublicOrPrivate_image$key;
  size: AvatarSize;
}) {
  return (
    <PublicOrPrivateImage
      pointerEvents="none"
      image={props.image}
      style={{
        width: "100%",
        height: "100%",
        ...{ pointerEvents: "none" },
      }}
    />
  );
}

export function EmployeeAvatar(props: {
  employee: Avatar_employee$key | null;
  size: AvatarSize;
  disableBorder?: boolean;
  style?: "assigned-to-me" | "default";
}) {
  const style = props.style ?? "default";

  const employee = useFragment(
    graphql`
      fragment Avatar_employee on Employee {
        image {
          ...ImagePublicOrPrivate_image
        }
        workStatus
        name
      }
    `,
    props.employee
  );
  const activeBadge = employee?.workStatus === "WORKING" ? true : false;
  if (!employee) {
    return (
      <AvatarCircle
        size={props.size}
        background="solid"
        disableBorder={props.disableBorder}
        style={style}
        activeBadge={activeBadge}
      >
        <AvatarIcon icon="guest-actor" size={props.size} style={style} />
      </AvatarCircle>
    );
  }

  const initials = (
    <AvatarInitials fullName={employee.name} size={props.size} style={style} />
  );

  if (employee.image) {
    return (
      <AvatarCircle
        size={props.size}
        background="image"
        disableBorder={props.disableBorder}
        style={style}
        activeBadge={activeBadge}
      >
        <AvatarImage image={employee.image} size={props.size} />
      </AvatarCircle>
    );
  }
  return (
    <AvatarCircle
      size={props.size}
      background="solid"
      disableBorder={props.disableBorder}
      style={style}
      activeBadge={activeBadge}
    >
      {initials}
    </AvatarCircle>
  );
}

/*
 Mostly for legacy users and HQ users

*/
export function UserAvatar(
  props: {
    user: Avatar_user$key;
  } & SharedAvatarProps
) {
  const style = props.style ?? "default";

  const user = useFragment(
    graphql`
      fragment Avatar_user on User {
        name
      }
    `,
    props.user
  );

  return (
    <AvatarCircle
      size={props.size}
      background="solid"
      disableBorder={props.disableBorder}
      style={style}
    >
      <AvatarInitials fullName={user.name} size={props.size} style={style} />
    </AvatarCircle>
  );
}
export function GuestAvatar(props: SharedAvatarProps) {
  const style = props.style ?? "default";
  return (
    <AvatarCircle
      size={props.size}
      background="solid"
      disableBorder={props.disableBorder}
      style={style}
    >
      <AvatarIcon icon="guest-actor" size={props.size} style={style} />
    </AvatarCircle>
  );
}

export function UserOrEmployeeAvatar(
  props: {
    user: Avatar_user$key | null;
    employee: Avatar_employee$key | null;
  } & SharedAvatarProps
) {
  if (props.employee) {
    return (
      <EmployeeAvatar
        employee={props.employee}
        size={props.size}
        style={props.style}
      />
    );
  }
  if (props.user) {
    return (
      <UserAvatar user={props.user} size={props.size} style={props.style} />
    );
  }
  return null;
}
function ScreenAvatar(props: SharedAvatarProps) {
  const style = props.style ?? "default";
  return (
    <AvatarCircle
      size={props.size}
      background="solid"
      disableBorder={props.disableBorder}
      style={style}
    >
      <AvatarIcon icon="tv" size={props.size} style={style} />
    </AvatarCircle>
  );
}

function GroupAvatar(
  props: SharedAvatarProps & { actor: Avatar_groupActor$key }
) {
  const group = useFragment(
    graphql`
      fragment Avatar_groupActor on GroupActor {
        employee {
          name
          ...Avatar_employee
        }
      }
    `,
    props.actor
  );
  const style = props.style ?? "default";
  if (group.employee) {
    return (
      <EmployeeAvatar
        employee={group.employee}
        size={props.size}
        style={props.style}
      />
    );
  }
  return (
    <AvatarCircle
      size={props.size}
      background="solid"
      disableBorder={props.disableBorder}
      style={style}
    >
      <AvatarIcon icon="guest-actor" size={props.size} style={style} />
    </AvatarCircle>
  );
}

function ConnectAvatar(
  props: SharedAvatarProps & { actor: Avatar_connectActor$key }
) {
  const connect = useFragment(
    graphql`
      fragment Avatar_connectActor on ConnectActor {
        employee {
          name
          ...Avatar_employee
        }
      }
    `,
    props.actor
  );
  const style = props.style ?? "default";
  if (connect.employee) {
    return (
      <EmployeeAvatar
        employee={connect.employee}
        size={props.size}
        style={props.style}
      />
    );
  }
  return (
    <AvatarCircle
      size={props.size}
      background="solid"
      disableBorder={props.disableBorder}
      style={style}
    >
      <AvatarIcon icon="connect" size={props.size} style={style} />
    </AvatarCircle>
  );
}

export function ActorAvatar({
  actor: actorFragment,
  ...rest
}: SharedAvatarProps & { actor: Avatar_actor$key }) {
  const actor = useFragment(
    graphql`
      fragment Avatar_actor on Actor {
        ... on UserActor {
          __typename
          storeUser {
            employee {
              name
              ...Avatar_employee
            }
            user {
              name
              ...Avatar_user
            }
          }
        }
        ... on ScreenActor {
          __typename
          screen {
            name
          }
        }
        ... on GroupActor {
          __typename
          ...Avatar_groupActor
        }
        ... on ConnectActor {
          __typename
          ...Avatar_connectActor
        }
      }
    `,
    actorFragment
  );
  switch (actor.__typename) {
    case "UserActor":
      return (
        <UserOrEmployeeAvatar
          user={actor.storeUser?.user ?? null}
          employee={actor.storeUser?.employee ?? null}
          {...rest}
        />
      );
    case "ScreenActor":
      return <ScreenAvatar {...rest} />;
    case "GroupActor":
      return <GroupAvatar actor={actor} {...rest} />;
    case "ConnectActor":
      return <ConnectAvatar actor={actor} {...rest} />;
    default:
      return null;
  }
}
